import type { ObjectType } from 'src/tpbp-configuration/models/models.const'
import { EQUIVALENT_OBJECT_TYPES } from 'src/tpbp-configuration/models/models.const'
import { StoreDictionary } from 'src/tpbp-json-api-handler/utils/StoreDictionary/StoreDictionary'
import _ from 'underscore'
import type {
  FieldsAndRelationsByOriginKey,
  FieldsByOriginKey,
} from './mapping.types'
import type {
  FieldInterface,
  RelationInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'

export const storeDictionary = new StoreDictionary()
export const storeDictionaryFieldsByOriginKey = new StoreDictionary()

const mappingFields: Record<string, FieldInterface[]> = {}
const mappingRelations: Record<string, RelationInterface[]> = {}

function ensureRegistration() {
  // Statically imported at bottom of the file
}

export function registerMapping(
  objectType: string,
  fields: FieldInterface[],
  relations: RelationInterface[],
) {
  mappingFields[objectType] = fields
  mappingRelations[objectType] = relations
}

export function getField(objectType: ObjectType, key: FieldInterface['key']) {
  const fieldsById = getFieldByKey(objectType)
  return fieldsById[key]
}

export function getFieldByKey(objectType: ObjectType) {
  const STORAGE_KEY = 'fieldsById'
  if (storeDictionary.has(STORAGE_KEY, objectType)) {
    const fields = storeDictionary.get<
      Record<FieldInterface['key'], FieldInterface>
    >(STORAGE_KEY, objectType)
    if (!fields)
      throw new Error(
        `Failed to get fields in storeDictionary for objectType '${objectType}'`,
      )
    return fields
  }
  const fields = getFields(objectType)
  const fieldsById: Record<FieldInterface['key'], FieldInterface> = _.indexBy(
    fields,
    'key',
  )
  storeDictionary.set(STORAGE_KEY, objectType, fieldsById)
  return fieldsById
}

export function getFields(objectType: ObjectType) {
  ensureRegistration()
  const fields = mappingFields[objectType]
  if (fields === undefined)
    throw new Error(`"${objectType}" does not exist in mappingFields`)
  return fields
}

export function getRelations(objectType: ObjectType) {
  ensureRegistration()
  const relations = mappingRelations[objectType]
  if (relations === undefined)
    throw new Error(`"${objectType}" does not exist in mappingRelations`)
  return relations
}

export function _getFieldMapping(objectType: ObjectType) {
  const fields = getFields(objectType)
  return fields.reduce<
    Record<FieldInterface['key'], FieldInterface['originKey']>
  >((mapping, field) => {
    mapping[field.key] = field.originKey
    return mapping
  }, {})
}

export function getFieldMapping(objectType: ObjectType) {
  const type = 'field'
  if (!storeDictionary.has(type, objectType)) {
    const mapping = _getFieldMapping(objectType)
    storeDictionary.set(type, objectType, mapping)
  }
  const mapping = storeDictionary.get<ReturnType<typeof _getFieldMapping>>(
    type,
    objectType,
  )
  if (!mapping)
    throw new Error(
      `Failed to get field mapping for objectType '${objectType}'`,
    )
  return mapping
}

export function _getRelationshipMapping(objectType: ObjectType) {
  const relationships = getRelations(objectType)
  return relationships.reduce<
    Record<RelationInterface['key'], RelationInterface>
  >((mapping, relationship) => {
    mapping[relationship.key] = relationship
    return mapping
  }, {})
}

export function getRelationshipMapping(objectType: ObjectType) {
  const type = 'relationship'
  if (!storeDictionary.has(type, objectType)) {
    const mapping = _getRelationshipMapping(objectType)
    storeDictionary.set(type, objectType, mapping)
  }
  const mapping = storeDictionary.get<
    ReturnType<typeof _getRelationshipMapping>
  >(type, objectType)
  if (!mapping)
    throw new Error(
      `Failed to get relationship mapping for objectType '${objectType}'`,
    )
  return mapping
}

export function getFieldsByOriginKey(objectType: ObjectType) {
  if (storeDictionaryFieldsByOriginKey.has('fieldsByOriginKey', objectType)) {
    const cache = storeDictionaryFieldsByOriginKey.get<FieldsByOriginKey>(
      'fieldsByOriginKey',
      objectType,
    )
    if (!cache)
      throw new Error(
        `Failed to get fieldsByOriginKey in storeDictionary for objectType '${objectType}'`,
      )
    return cache
  }
  const fields = getFields(objectType)
  const fieldsByOriginKey: FieldsByOriginKey = _.indexBy(fields, 'originKey')
  storeDictionaryFieldsByOriginKey.set(
    'fieldsByOriginKey',
    objectType,
    fieldsByOriginKey,
  )
  return fieldsByOriginKey
}

export function getFieldsAndRelationsByOriginKey(objectType: ObjectType) {
  if (
    storeDictionaryFieldsByOriginKey.has(
      'fieldsAndRelationsByOriginKey',
      objectType,
    )
  ) {
    const cache =
      storeDictionaryFieldsByOriginKey.get<FieldsAndRelationsByOriginKey>(
        'fieldsAndRelationsByOriginKey',
        objectType,
      )
    if (!cache)
      throw new Error(
        `Failed to get fieldsAndRelationsByOriginKey in storeDictionary for objectType '${objectType}'`,
      )
    return cache
  }
  const fields = getFields(objectType)
  const relations = getRelations(objectType)
  const fieldsAndRelations = [...fields, ...relations]
  const fieldsAndRelationsByOriginKey: Record<
    FieldInterface['originKey'],
    FieldInterface | RelationInterface
  > = _.indexBy(fieldsAndRelations, 'originKey')
  storeDictionaryFieldsByOriginKey.set(
    'fieldsAndRelationsByOriginKey',
    objectType,
    fieldsAndRelationsByOriginKey,
  )
  return fieldsAndRelationsByOriginKey
}

export function getEquivalentType(objectType: string): ObjectType {
  return EQUIVALENT_OBJECT_TYPES[objectType] ?? objectType
}

import { MAPPING_FIELDS, MAPPING_RELATIONS } from './mapping.const'

export function registerAllMappings() {
  for (const objectType of Object.keys(MAPPING_FIELDS)) {
    registerMapping(
      objectType,
      MAPPING_FIELDS[objectType as ObjectType] || [],
      MAPPING_RELATIONS[objectType as ObjectType] || [],
    )
  }
}

registerAllMappings()
